// using copilot-instructions: RouterOperationsSidebar component following financial software standards
import React, { useState, useEffect, useMemo } from "react";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Tooltip } from "primereact/tooltip";
import { useToast } from "../ToastProvider";
import { router } from "../../data/Services/ordersRouter";
import { useChartsStore } from "../../store/chartStore";
import { useOrderStore } from "../../store/orderStore.jsx";
import { useOrderOverlay } from "../../hooks/useOrderOverlay";
import { useOrderDefaults } from "../../hooks/useOrderDefaults";
import {
  confirmCancelOrdersForSymbol,
  confirmClosePosition,
} from "../../utils/confirmationUtils.js";
import marketdata from "../../data/Services/marketdata";
import RouterLoginModal from "../Modals/RouterLoginModal";
import { getTickSize, computeReaisDistance } from "../../utils/priceUtils.js";
import { buildOtoLegs } from "../../utils/otoLegs.js";
import { submitOrderToRouter } from "../../utils/orderSubmission.js";
import "./RouterOperationsSidebar.css";
import {
  cancelLocalOrdersForSymbol,
  cancelRouterOrdersForSymbol,
  closePositionsForSymbol,
} from "../../utils/orderCancellation.js";
import { isMobilePhone } from "../../utils/mobileDetection";
import useWorkspaceStore from "../../store/workspaceStore";
import useBookByPriceStore from "../../store/bookByPriceStore";

const RouterOperationsSidebar = () => {
  // Hide entire sidebar on mobile phones - trading not available
  if (isMobilePhone()) {
    return null;
  }

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const { showError } = useToast();

  // Disable routing when a non-trading panel (log, positions, openOrders, watchlist) is focused.
  const focusedPanelType = useWorkspaceStore((state) => state.focusedPanelType);
  const isChartFocused = focusedPanelType === "chart";
  const isBookByPriceFocused = focusedPanelType === "bookByPrice";
  const bookByPriceViewMode = useBookByPriceStore((state) => state.viewMode);
  const isSuperDomFocused =
    isBookByPriceFocused && bookByPriceViewMode === "superdom";
  const isTradingPanelFocused = isChartFocused || isSuperDomFocused;

  // Get current trading symbol. Charts use the focused chart; Books use the active Book instance.
  const selectedChart = useChartsStore((state) => state.getSelectedChart());
  const bookByPriceSymbol = useBookByPriceStore((state) => state.symbol);
  const currentSymbol = isBookByPriceFocused
    ? bookByPriceSymbol
    : selectedChart?.symbol || "";

  // Get order store methods - use stable selectors
  const ordersBySymbol = useOrderStore((state) => state.ordersBySymbol);
  const getOrdersForSymbol = useOrderStore((state) => state.getOrdersForSymbol);
  const removeFromStore = useOrderStore((state) => state.removeOrder);
  const clearOrdersForSymbol = useOrderStore(
    (state) => state.clearOrdersForSymbol,
  );
  const {
    removeOrder: removeFromOverlay,
    orders: overlayOrders,
    createOrder,
  } = useOrderOverlay(currentSymbol);
  const updateOrderInStore = useOrderStore((state) => state.updateOrder);

  // Quantity/OTO are per symbol, shared between chart and Book panels.
  const {
    quantity,
    setQuantity,
    otoEnabled,
    otoLossTicks,
    otoProfitTicks,
    setOtoEnabled,
    setOtoLossTicks,
    setOtoProfitTicks,
  } = useOrderDefaults(currentSymbol);

  const lossLabel = useMemo(() => {
    if (!currentSymbol) return "—";
    const tickSize = getTickSize(currentSymbol);
    const points = otoLossTicks * tickSize;
    const reais = computeReaisDistance(currentSymbol, 0, points, quantity);
    if (reais == null) return `${points} pts`;
    return `${points} pts · R$${Math.abs(reais).toFixed(2)}`;
  }, [currentSymbol, otoLossTicks, quantity]);

  const profitLabel = useMemo(() => {
    if (!currentSymbol) return "—";
    const tickSize = getTickSize(currentSymbol);
    const points = otoProfitTicks * tickSize;
    const reais = computeReaisDistance(currentSymbol, 0, points, quantity);
    if (reais == null) return `${points} pts`;
    return `${points} pts · R$${Math.abs(reais).toFixed(2)}`;
  }, [currentSymbol, otoProfitTicks, quantity]);

  // Handle ESC key and chart clicks to blur quantity input
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        // Find the quantity input element
        const quantityInput = document.querySelector(".quantity-input input");
        if (quantityInput) {
          quantityInput.blur();
        }
      }
    };

    const handleChartClick = (event) => {
      // Check if click is on chart elements (not on the sidebar or other UI elements)
      const target = event.target;
      const isChartElement = target.closest(
        ".chart-container, .chart-canvas, .chart-wrapper, canvas, svg",
      );
      const isSidebarElement = target.closest(".router-operations-sidebar");
      const isModalElement = target.closest(
        ".p-dialog, .p-overlaypanel, .p-menu",
      );

      if (isChartElement && !isSidebarElement && !isModalElement) {
        // Find the quantity input element and blur it
        const quantityInput = document.querySelector(".quantity-input input");
        if (quantityInput) {
          quantityInput.blur();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleChartClick);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleChartClick);
    };
  }, []);

  // Handle quantity changes with standard lot validation
  const handleQuantityChange = (value) => {
    if (!value || !currentSymbol) {
      setQuantity(value || 0, currentSymbol);
      return;
    }

    // Use router's adjustStandardLot to enforce constraints
    const adjustedQuantity = router.adjustStandardLot(currentSymbol, value);
    // Ensure we never set quantity to 0 or less than standard lot
    const finalQuantity = Math.max(
      adjustedQuantity,
      router.adjustStandardLot(currentSymbol, 1),
    );
    setQuantity(finalQuantity, currentSymbol);
  };

  const operationPairs = [
    {
      buy: {
        id: "buy-market",
        label: "COMPRA",
        type: "buy",
        icon: "▲",
        description: "Ordem de Compra a Mercado",
      },
      sell: {
        id: "sell-market",
        label: "VENDA",
        type: "sell",
        icon: "▼",
        description: "Ordem de Venda a Mercado",
      },
      pairLabel: "MERCADO",
    },
    {
      buy: {
        id: "buy-order",
        label: "COMPRA",
        type: "buy",
        icon: "⬆",
        description: "Ordem de Compra (Preço Atual - 1 Tick)",
      },
      sell: {
        id: "sell-order",
        label: "VENDA",
        type: "sell",
        icon: "⬇",
        description: "Ordem de Venda (Preço Atual + 1 Tick)",
      },
      pairLabel: "ORDEM",
    },
  ];

  const closeOperation = {
    id: "close-position",
    label: "ZERAR CANCELAR",
    type: "neutral",
    icon: "✕",
    description: "Zerar/Cancelar Posição/Ordens do Símbolo Atual",
  };

  const cancelOperation = {
    id: "cancel-orders",
    label: "CANCELAR ORDENS",
    type: "warning",
    icon: "⊘",
    description: "Cancelar Ordens do Símbolo Atual",
  };

  // Helper function to get current market price for symbol
  const getCurrentPrice = () => {
    if (!currentSymbol) return null;
    return marketdata.getLastQuote(currentSymbol)?.C || null;
  };

  // Helper function to calculate buy price (current price - 1 minimum increment)
  const calculateBuyPrice = () => {
    const currentPrice = getCurrentPrice();
    if (!currentPrice || !currentSymbol) return null;

    const minIncrement = marketdata.getMinInc(currentSymbol);
    const buyPrice = currentPrice - minIncrement;

    console.log(
      `📊 Buy price calculation: ${currentPrice} - ${minIncrement} = ${buyPrice}`,
    );
    return buyPrice;
  };

  // Helper function to calculate sell price (current price + 1 minimum increment)
  const calculateSellPrice = () => {
    const currentPrice = getCurrentPrice();
    if (!currentPrice || !currentSymbol) return null;

    const minIncrement = marketdata.getMinInc(currentSymbol);
    const sellPrice = currentPrice + minIncrement;

    console.log(
      `📊 Sell price calculation: ${currentPrice} + ${minIncrement} = ${sellPrice}`,
    );
    return sellPrice;
  };

  // Helper function to create local limit order
  const createLocalOrder = (side, price) => {
    if (!currentSymbol || !price || !quantity) {
      console.warn("Cannot create order: missing symbol, price, or quantity");
      return;
    }

    const newOrder = createOrder(
      currentSymbol,
      side,
      price,
      quantity,
      getCurrentPrice(),
    );
    if (!newOrder) return null;

    // Attach OTO bracket legs when armed
    if (otoEnabled) {
      const legs = buildOtoLegs({
        mainOrderId: newOrder.id,
        symbol: newOrder.symbol,
        side,
        quantity,
        anchorPrice: newOrder.price,
        lossTicks: otoLossTicks,
        profitTicks: otoProfitTicks,
      });
      if (legs.length) {
        updateOrderInStore(newOrder.symbol, newOrder.id, { oto: legs });
      }
    }

    return newOrder;
  };

  const handleCancelAllOrders = async (e) => {
    if (!isTradingPanelFocused) return;

    console.log(`🎯 BUTTON CLICKED: handleCancelAllOrders called!`);

    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (router.state !== "online") {
      setShowLoginModal(true);
      return;
    }

    if (!currentSymbol) {
      alert("Selecione um símbolo primeiro");
      return;
    }

    confirmCancelOrdersForSymbol(currentSymbol, async () => {
      console.log(
        `🚀 CANCELAR ORDENS: Starting cancellation for symbol ${currentSymbol}`,
      );

      try {
        // Cancel router orders for the symbol using centralized utility
        await cancelRouterOrdersForSymbol(currentSymbol);

        // Cancel local orders for the symbol using centralized utility
        cancelLocalOrdersForSymbol(
          currentSymbol,
          ordersBySymbol,
          removeFromStore,
          overlayOrders,
          removeFromOverlay,
          getOrdersForSymbol,
        );

        console.log(`🏁 CANCELAR ORDENS: Process completed successfully`);

        // Success message will come from server via receiveInfo
      } catch (error) {
        console.error("❌ CANCELAR ORDENS: Erro ao cancelar ordens:", error);
        console.error("❌ Error stack:", error.stack);
        showError(
          `Erro ao cancelar ordens: ${error.message}`,
          "Erro de Cancelamento",
        );
        if (
          error.message.includes("401") ||
          error.message.includes("Unauthorized") ||
          error.message.includes("login")
        ) {
          setShowLoginModal(true);
        }
      }
    });
  };

  const handleClosePosition = async () => {
    console.log(
      `🎯 BUTTON CLICKED: handleClosePosition called for symbol ${currentSymbol}!`,
    );

    if (router.state !== "online") {
      setShowLoginModal(true);
      return;
    }

    if (!currentSymbol) {
      alert("Selecione um símbolo primeiro");
      return;
    }

    confirmClosePosition(currentSymbol, async () => {
      console.log(
        `🚀 ZERAR/CANCelar POSIÇÃO: Starting position close and order cancellation for symbol ${currentSymbol}`,
      );

      try {
        // 1. Cancel local orders for the current symbol using centralized utility
        cancelLocalOrdersForSymbol(
          currentSymbol,
          ordersBySymbol,
          removeFromStore,
          overlayOrders,
          removeFromOverlay,
          getOrdersForSymbol,
        );

        // 2. Cancel open orders at the broker (router orders)
        await cancelRouterOrdersForSymbol(currentSymbol);

        // 3. Close the position using centralized utility
        await closePositionsForSymbol(currentSymbol);

        // 4. Force clear ALL orders for the symbol (and related symbols) from local store
        // This ensures orphaned orders are removed even if account data sync fails
        const translatedSymbol = router.translateFutSymbol(currentSymbol);
        const symbolsToClean = new Set([currentSymbol, translatedSymbol]);

        // Find all related symbols (same 3-letter prefix for futures)
        const prefix = currentSymbol.substring(0, 3);
        Object.keys(ordersBySymbol).forEach((sym) => {
          if (sym.startsWith(prefix)) {
            symbolsToClean.add(sym);
          }
        });

        console.log(
          `🧹 Force clearing orders for symbols:`,
          Array.from(symbolsToClean),
        );
        symbolsToClean.forEach((sym) => {
          clearOrdersForSymbol(sym);
        });

        // 5. Also force account data refresh to ensure full sync
        setTimeout(async () => {
          try {
            router._lastAccountDataString = null;
            await router.getAccountDataAsync();
            console.log("🔄 Account data refreshed after zerar/cancelar");
          } catch (e) {
            console.warn("Failed to refresh account data:", e);
          }
        }, 500);

        console.log(
          `🏁 ZERAR/CANCELAR POSIÇÃO: Process completed successfully`,
        );

        // Success message will come from server via receiveInfo
      } catch (error) {
        console.error(
          "❌ ZERAR/CANCELAR POSIÇÃO: Erro ao zerar/cancelar posição:",
          error,
        );
        console.error("❌ Error stack:", error.stack);
        showError(
          `Erro ao zerar/cancelar posição: ${error.message}`,
          "Erro de Fechamento",
        );
        if (
          error.message.includes("401") ||
          error.message.includes("Unauthorized") ||
          error.message.includes("login")
        ) {
          setShowLoginModal(true);
        }
      }
    });
  };

  const handleOperationClick = async (operation) => {
    if (!isTradingPanelFocused) return;

    if (router.state !== "online") {
      setShowLoginModal(true);
      return;
    }

    if (!currentSymbol) {
      alert("Selecione um símbolo primeiro");
      return;
    }

    setSelectedOperation(operation.id);

    try {
      // Generate unique order ID
      const orderId = crypto.randomUUID
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c == "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });

      // Keep the FUT/FUL alias here — translation AND the 08:55 freshness gate run
      // at the single submit chokepoint (convertOrderObjectToRouterOrder →
      // resolveSymbolForTrading). Pre-resolving here would bypass the gate (#351).
      const orderSymbol = currentSymbol;

      // Determine side and handle non-market operations first
      let side = null;
      switch (operation.id) {
        case "buy-market":
          side = "buy";
          break;
        case "sell-market":
          side = "sell";
          break;
        case "buy-order": {
          const buyPrice = calculateBuyPrice();
          if (buyPrice) {
            createLocalOrder("buy", buyPrice);
          } else {
            alert(
              "Não foi possível obter o preço atual do mercado para criar a ordem de compra",
            );
          }
          return;
        }
        case "sell-order": {
          const sellPrice = calculateSellPrice();
          if (sellPrice) {
            createLocalOrder("sell", sellPrice);
          } else {
            alert(
              "Não foi possível obter o preço atual do mercado para criar a ordem de venda",
            );
          }
          return;
        }
        case "close-position":
          await handleClosePosition();
          return;
        case "cancel-orders":
          alert("Use o botão específico de cancelar ordens");
          return;
        default:
          alert("Operação não reconhecida");
          return;
      }

      // Build standardized market order (MERCADO path)
      const standardOrder = {
        id: orderId,
        symbol: orderSymbol,
        side,
        quantity,
        orderType: "MARKET",
      };

      // Attach OTO bracket when armed — anchor at last quote price
      if (otoEnabled) {
        const anchorPrice = getCurrentPrice();
        if (!anchorPrice) {
          alert(
            "Preço não disponível. Aguarde a cotação antes de enviar ordem OTO.",
          );
          return;
        }
        const legs = buildOtoLegs({
          mainOrderId: orderId,
          symbol: orderSymbol,
          side,
          quantity,
          anchorPrice,
          lossTicks: otoLossTicks,
          profitTicks: otoProfitTicks,
        });
        if (!legs.length) {
          alert(
            "Não foi possível calcular ordens OTO. Verifique os parâmetros.",
          );
          return;
        }
        standardOrder.oto = legs;
      }

      // Submit via shared utility — handles conversion + router.placeOrder
      await submitOrderToRouter(
        standardOrder,
        () => setShowLoginModal(true),
        () => {
          console.log(`✅ Ordem ${operation.id} enviada com sucesso`);
        },
        (error) => {
          throw error;
        },
      );
      console.log(`✅ Ordem ${operation.id} enviada:`, standardOrder);
    } catch (error) {
      console.error("❌ Erro ao enviar ordem:", error);
      alert(`Erro ao enviar ordem: ${error.message}`);
    } finally {
      // Reset button state
      setTimeout(() => setSelectedOperation(null), 150);
    }
  };

  return (
    <>
      <Tooltip target=".router-quantity-input" />

      <div className="router-operations-sidebar">
        <div className="order-entry-section">
          <div className="quantity-section">
            <label className="quantity-label">QUANTIDADE</label>
            <InputNumber
              value={quantity}
              onValueChange={(e) => handleQuantityChange(e.value)}
              min={1}
              max={999999}
              maxFractionDigits={0}
              minFractionDigits={0}
              step={
                currentSymbol ? router.adjustStandardLot(currentSymbol, 1) : 1
              }
              showButtons
              buttonLayout="vertical"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
              disabled={!isTradingPanelFocused}
              className="quantity-input router-quantity-input"
              data-pr-tooltip={
                currentSymbol
                  ? `Quantidade será ajustada para múltiplos do lote padrão (${router.adjustStandardLot(currentSymbol, 1)})`
                  : "Selecione um símbolo para ver as restrições de lote"
              }
              data-pr-position="top"
              data-evo-id="routeroperationssidebar-input-quantity"
            />
          </div>

          <div className="operations-section">
            <div className="section-header">ORDENS RÁPIDAS</div>

            {operationPairs.map((pair, index) => (
              <React.Fragment key={index}>
                <div className="operation-subsection-label">
                  {pair.pairLabel}
                </div>

                <button
                  className={`operation-button ${pair.buy.type} stacked ${
                    selectedOperation === pair.buy.id ? "active" : ""
                  }`}
                  onClick={() => handleOperationClick(pair.buy)}
                  disabled={!isTradingPanelFocused}
                  aria-label={pair.buy.description}
                  title={pair.buy.description}
                  data-evo-id={`routeroperationssidebar-btn-buy-${pair.pairLabel.toLowerCase()}`}
                >
                  <span className="operation-icon">{pair.buy.icon}</span>
                  <span className="operation-label">{pair.buy.label}</span>
                </button>

                <button
                  className={`operation-button ${pair.sell.type} stacked ${
                    selectedOperation === pair.sell.id ? "active" : ""
                  }`}
                  onClick={() => handleOperationClick(pair.sell)}
                  disabled={!isTradingPanelFocused}
                  aria-label={pair.sell.description}
                  title={pair.sell.description}
                  data-evo-id={`routeroperationssidebar-btn-sell-${pair.pairLabel.toLowerCase()}`}
                >
                  <span className="operation-icon">{pair.sell.icon}</span>
                  <span className="operation-label">{pair.sell.label}</span>
                </button>
              </React.Fragment>
            ))}

            <div className="close-section">
              <button
                className={`operation-button ${closeOperation.type} full-width ${
                  selectedOperation === closeOperation.id ? "active" : ""
                }`}
                onClick={() => handleOperationClick(closeOperation)}
                disabled={!isTradingPanelFocused}
                aria-label={closeOperation.description}
                title={closeOperation.description}
                data-evo-id="routeroperationssidebar-btn-close-position"
              >
                <span className="operation-icon">{closeOperation.icon}</span>
                <span className="operation-label">{closeOperation.label}</span>
              </button>

              <button
                className={`operation-button ${cancelOperation.type} full-width ${
                  selectedOperation === cancelOperation.id ? "active" : ""
                }`}
                onClick={handleCancelAllOrders}
                disabled={!isTradingPanelFocused}
                aria-label={cancelOperation.description}
                title={cancelOperation.description}
                data-evo-id="routeroperationssidebar-btn-cancel-orders"
              >
                <span className="operation-icon">{cancelOperation.icon}</span>
                <span className="operation-label">{cancelOperation.label}</span>
              </button>
            </div>
          </div>

          <div className="oto-section">
            <div className="oto-header">
              <span className="oto-section-label">OTO</span>
              <InputSwitch
                checked={!!otoEnabled}
                onChange={(e) => setOtoEnabled(e.value)}
                disabled={!isTradingPanelFocused}
                className="oto-switch"
                data-evo-id="routeroperationssidebar-switch-oto"
              />
            </div>
            <div className={`oto-inputs${!otoEnabled ? " oto-disabled" : ""}`}>
              <label className="oto-input-label">PERDA</label>
              <InputNumber
                value={otoLossTicks}
                onValueChange={(e) => setOtoLossTicks(e.value)}
                min={1}
                max={9999}
                maxFractionDigits={0}
                minFractionDigits={0}
                step={1}
                showButtons
                buttonLayout="vertical"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
                disabled={!isTradingPanelFocused || !otoEnabled}
                className="oto-input"
                data-evo-id="routeroperationssidebar-input-oto-loss"
              />
              <span className="oto-distance-label">{lossLabel}</span>
              <label className="oto-input-label">GANHO</label>
              <InputNumber
                value={otoProfitTicks}
                onValueChange={(e) => setOtoProfitTicks(e.value)}
                min={1}
                max={9999}
                maxFractionDigits={0}
                minFractionDigits={0}
                step={1}
                showButtons
                buttonLayout="vertical"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
                disabled={!isTradingPanelFocused || !otoEnabled}
                className="oto-input"
                data-evo-id="routeroperationssidebar-input-oto-profit"
              />
              <span className="oto-distance-label">{profitLabel}</span>
            </div>
          </div>
        </div>

        <RouterLoginModal
          visible={showLoginModal}
          onHide={() => setShowLoginModal(false)}
        />
      </div>
    </>
  );
};

export default RouterOperationsSidebar;
